import { useMemo } from 'react';
import { ballColor, ballColorDark } from '@photo-merge/shared';
import { pickStockBalls, stockUrl } from '../game/art';

interface BallMarkProps {
  size?: number;
  className?: string;
}

/** The mark always draws from a full-size set's palette, whatever a given game uses. */
const PALETTE_SIZE = 15;

// x, y, r, rank — a settled little heap. Ranks picked for a pleasant spread out of the
// 15-ball palette (mint, coral, blue, pink, purple), skipping its yellow band.
const BALLS: Array<[number, number, number, number]> = [
  [30, 66, 22, 13],
  [70, 70, 18, 1],
  [52, 32, 26, 2],
  [22, 30, 14, 12],
  [80, 34, 11, 8],
];

/**
 * The app's mark: a pile of balls drawn with the same ring and highlight treatment as
 * the game canvas, so the brand and the gameplay look like one thing. Real stock faces
 * fill them, which says what the game is faster than any abstract logo would.
 */
export function BallMark({ size = 96, className }: BallMarkProps) {
  const faces = useMemo(() => pickStockBalls(BALLS.length), []);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className={className}
      role="img"
      aria-label="MergeBalls"
    >
      <defs>
        <radialGradient id="ballmark-shine" cx="35%" cy="28%" r="70%">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.7" />
          <stop offset="45%" stopColor="#fff" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        {BALLS.map(([cx, cy, r], i) => (
          // One clip per ball so each photo is confined to its own circle.
          <clipPath key={i} id={`ballmark-clip-${i}`}>
            <circle cx={cx} cy={cy} r={r * 0.84} />
          </clipPath>
        ))}
      </defs>

      {BALLS.map(([cx, cy, r, rank], i) => (
        <g key={rank}>
          <circle cx={cx} cy={cy} r={r} fill={ballColorDark(rank, PALETTE_SIZE)} />
          <circle cx={cx} cy={cy} r={r * 0.84} fill={ballColor(rank, PALETTE_SIZE)} />
          <image
            href={stockUrl(faces[i]!)}
            x={cx - r * 0.84}
            y={cy - r * 0.84}
            width={r * 1.68}
            height={r * 1.68}
            clipPath={`url(#ballmark-clip-${i})`}
            preserveAspectRatio="xMidYMid slice"
          />
          <circle cx={cx} cy={cy} r={r * 0.84} fill="url(#ballmark-shine)" />
        </g>
      ))}
    </svg>
  );
}
